import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import type { AgreementField } from "@/types/agreement";
import { isDateExpired } from "@/lib/isDateExpired";

export const useAgreementFieldRenderer = (field: AgreementField) => {
  const { t } = useTranslation();
  const translateValue = useCallback(
    (value: unknown) => {
      if (value === undefined || value === null || value === "") {
        return "";
      }

      if (field.valueTranslationPrefix) {
        return t(`${field.valueTranslationPrefix}.${value}`);
      }

      return String(value);
    },
    [field.valueTranslationPrefix, t],
  );

  return useCallback(
    (value: unknown, data: Record<string, unknown>) => {
      if (field.type === "boolean") {
        return (
          <div className="py-1 text-xs text-gray-900 leading-tight">
            {t(`booleanValues.${Boolean(value).toString()}`)}
          </div>
        );
      }

      let textValue: string;

      if (field.formatValue) {
        textValue = field.formatValue(value, data, t);
      } else if (field.valueTranslationPrefix) {
        textValue = translateValue(value);
      } else {
        textValue = String(value ?? "");
      }

      const shouldShowExpiredWarning =
        field.showExpiredWarning && value ? isDateExpired(value) : false;

      const renderExpiredBadge = () => (
        <span
          title={t("questionnaires.warnings.expiredDocument")}
          className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[11px] font-medium text-orange-700"
        >
          <AlertTriangle className="h-3 w-3" />
          {t("questionnaires.warnings.expired")}
        </span>
      );

      return (
        <div className="py-1 text-xs text-gray-900 leading-tight">
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
            <span className="whitespace-pre-wrap break-words">{textValue}</span>
            {shouldShowExpiredWarning && renderExpiredBadge()}
          </div>
        </div>
      );
    },
    [
      field.formatValue,
      field.showExpiredWarning,
      field.type,
      field.valueTranslationPrefix,
      t,
      translateValue,
    ],
  );
};
